// Eskom PARCC — pure scope + filter + search for the Working Papers list.
//
// The Working Papers screen lists *allocated* transactions (those carrying an
// `assignedToEmail`), each joined to its working paper (by `referenceNo`) for
// the captured review status / compliance / notes. Two pure helpers drive it,
// both kept free of React and fetching so they unit-test in isolation:
//
//   • `scopeWorkingPapers` — the role boundary. Reviewers see ONLY transactions
//     allocated to their own email; admins see every allocated transaction.
//     Email identity is compared case-insensitively via `normalizeEmail`, so a
//     reviewer signed in as `Jane@Eskom.co.za` still matches an allocation to
//     `jane@eskom.co.za`. This mirrors the monolith's `assignedRows` + role gate.
//   • `filterWorkingPapers` — the in-screen Review-Status select + free-text
//     search, narrowing the already-scoped rows.
//
// Each row pairs a transaction with its optional working paper, so the page can
// render captured fields without a per-row scan. Compliance / review status fall
// back from the working paper to the transaction (the monolith captured these on
// the transaction; the SQL-native model moves them to the working paper).
import type { Transaction } from '../../data/types';
import type { WorkingPaperRead } from '../../lib/db';
import { normalizeEmail } from '../../lib/email';
import { canViewTransaction, type Grant } from '../../lib/permissions';

/** A transaction paired with its working paper (if one has been captured yet). */
export interface WorkingPaperRow {
  tx: Transaction;
  wp: WorkingPaperRead | undefined;
}

export interface WorkingPaperFilters {
  /** Matched against the row's effective review status (exact). */
  reviewStatus: string;
  /** Matched against the transaction's Buyer (exact). (Feedback 2b.) */
  buyer: string;
  /** Matched against the allocated reviewer (assignedToEmail, case-insensitive). (Feedback 2b.) */
  user: string;
  /** Free-text search across ref / supplier / division / assignee / notes. */
  search: string;
}

export const EMPTY_WORKING_PAPER_FILTERS: WorkingPaperFilters = {
  reviewStatus: '',
  buyer: '',
  user: '',
  search: '',
};

/** True when the transaction has been allocated to a reviewer. */
function isAllocated(tx: Transaction): boolean {
  return normalizeEmail(tx.assignedToEmail) !== '';
}

/** The review status shown/filtered: the working paper's, else the transaction's. */
export function effectiveReviewStatus(row: WorkingPaperRow): string {
  return row.wp?.reviewStatus ?? row.tx.reviewStatus ?? '';
}

/**
 * The compliance level shown: the verdict of record — the transaction's strict-rule
 * override (what the register treats as authoritative, and what the editors seed from)
 * — else the working paper's captured level, else the transaction's. A verdict given from
 * the transaction editor before 29 Sep 2026 lives on the override only, so reading the
 * paper first showed a stale self-declared level beside a register that read Compliant
 * (Lindiwe, 4600080858). `||` so a blank never masks a set value.
 */
export function effectiveWorkingPaperCompliance(row: WorkingPaperRow): string {
  return row.tx.complianceLevelOverride || row.wp?.complianceLevel || row.tx.complianceLevel || '';
}

/** The working-paper notes shown/searched: the working paper's, else the transaction's. */
export function workingPaperNotes(row: WorkingPaperRow): string {
  return row.wp?.workingPaperNotes ?? row.tx.workingPaperNotes ?? '';
}

/**
 * Build the scoped, joined row set for the Working Papers list.
 *
 * Only *allocated* (non-deleted) transactions are listed. VISIBILITY follows the
 * capability model (src/lib/permissions): cross-division roles (admin / manager /
 * dataCoordinator) see every allocated transaction; a teamLead / reviewer sees the
 * allocated transactions in their HOME division plus any allocated to them directly
 * (a cross-division allocation reveals that one row only, never the rest of that
 * division). A null grant (auth resolving) sees nothing. Edit-ability is gated
 * separately by {@link canEditTransaction}: a reviewer may SEE their division's
 * working papers but only CAPTURE their own. Each surviving transaction is joined
 * to its working paper by `referenceNo`.
 */
export function scopeWorkingPapers(
  transactions: readonly Transaction[],
  grant: Grant | null,
  workingPapers: readonly WorkingPaperRead[] = [],
): WorkingPaperRow[] {
  const wpByRef = new Map<string, WorkingPaperRead>();
  for (const wp of workingPapers) {
    if (wp.referenceNo && !wpByRef.has(wp.referenceNo)) wpByRef.set(wp.referenceNo, wp);
  }

  if (!grant) return [];
  return transactions
    .filter((tx) => {
      if (tx.deleted) return false;
      if (!isAllocated(tx)) return false;
      return canViewTransaction(tx, grant);
    })
    .map((tx) => ({ tx, wp: wpByRef.get(tx.referenceNo) }));
}

/** Lowercased haystack of the searchable fields for a working-paper row. */
function haystack(row: WorkingPaperRow): string {
  const { tx } = row;
  return [
    tx.referenceNo,
    tx.dataset,
    tx.division,
    tx.supplier,
    tx.assignedToEmail,
    effectiveReviewStatus(row),
    workingPaperNotes(row),
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
}

/**
 * Narrow already-scoped working-paper rows by the active filters. The
 * review-status select narrows by exact match against
 * {@link effectiveReviewStatus}; the search term narrows by case-insensitive
 * substring across {@link haystack}. Input order is preserved (the DataTable
 * owns sorting).
 */
export function filterWorkingPapers(
  rows: readonly WorkingPaperRow[],
  filters: WorkingPaperFilters,
): WorkingPaperRow[] {
  const q = filters.search.trim().toLowerCase();
  const user = normalizeEmail(filters.user);
  return rows.filter((row) => {
    if (filters.reviewStatus && effectiveReviewStatus(row) !== filters.reviewStatus) return false;
    // Feedback 2b — download/list working papers by buyer or by individual user.
    if (filters.buyer && (row.tx.buyer ?? '') !== filters.buyer) return false;
    if (user && normalizeEmail(row.tx.assignedToEmail) !== user) return false;
    if (q && !haystack(row).includes(q)) return false;
    return true;
  });
}
